import React, { useState } from 'react';
import 'date-fns';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, KeyboardTimePicker, KeyboardDatePicker } from '@material-ui/pickers';

import { Button, ButtonGroup } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  DateTimeContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  timePickerRow: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-around',
  },
  startDiv: {
    display: 'flex',
    flexDirection: 'column',
    margin: '0px 3px',
  },
});

export interface DateTimeRange {
  start: moment.Moment;
  end: moment.Moment;
}

interface DateTimeRangePickerProps {
  startDateTime: moment.Moment;
  endDateTime: moment.Moment;
  setDateTimeRange(dateTimeRange: DateTimeRange): void;
  setUpdated?(updated: boolean): void;
  setTooltipOpen?(open: boolean): void;
}

const DateTimeRangePicker: React.FC<DateTimeRangePickerProps> = ({
  startDateTime,
  endDateTime,
  setDateTimeRange,
  setUpdated = () => null,
  setTooltipOpen = () => {},
}: DateTimeRangePickerProps) => {
  // Datepicker
  const [startTime, setStartTime] = useState(startDateTime.clone().utc().startOf('minute').toDate());
  const [startDate, setStartDate] = useState(startDateTime.clone().utc().startOf('minute').toDate());
  const [endTime, setEndTime] = useState(endDateTime.clone().utc().startOf('minute').toDate());
  const [endDate, setEndDate] = useState(endDateTime.clone().utc().startOf('minute').toDate());

  const handleClick = () => {
    const newStartDateTime = moment(startDate).set({
      hour: moment(startTime).get('hour'),
      minute: moment(startTime).get('minute'),
      second: 0,
      millisecond: 0,
    });
    const newEndDateTime = moment(endDate).set({
      hour: moment(endTime).get('hour'),
      minute: moment(endTime).get('minute'),
      second: 0,
      millisecond: 0,
    });
    const dateTimeRange: DateTimeRange = {
      start: newStartDateTime,
      end: newEndDateTime,
    };
    setDateTimeRange(dateTimeRange);
    setTooltipOpen(false);
    setUpdated(true);
  };

  const classes = useStyles();

  return (
    <div className={classes.DateTimeContainer}>
      <div className={classes.timePickerRow}>
        <div className={classes.startDiv}>
          <h4>Start Date/Time:</h4>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              disableToolbar
              format="yyyy-MM-dd"
              margin="normal"
              id="startDatePicker"
              label="Start Date"
              value={startDate}
              onChange={(date) => {
                setStartDate(new Date(date !== null ? date.valueOf() : 0));
              }}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
            />
            <KeyboardTimePicker
              margin="normal"
              id="startTimePicker"
              label="Start Time"
              value={startTime}
              onChange={(time) => {
                setStartTime(new Date(time !== null ? time.valueOf() : 0));
              }}
              KeyboardButtonProps={{
                'aria-label': 'change time',
              }}
            />
          </MuiPickersUtilsProvider>
        </div>
        <div className={classes.startDiv}>
          <h4>End Date/Time:</h4>
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              disableToolbar
              format="yyyy-MM-dd"
              margin="normal"
              id="endDatePicker"
              label="End Date"
              value={endDate}
              onChange={(date) => {
                setEndDate(new Date(date !== null ? date.valueOf() : 0));
              }}
              KeyboardButtonProps={{
                'aria-label': 'change date',
              }}
            />
            <KeyboardTimePicker
              margin="normal"
              id="endTimePicker"
              label="End Time"
              value={endTime}
              onChange={(time) => {
                setEndTime(new Date(time !== null ? time.valueOf() : 0));
              }}
              KeyboardButtonProps={{
                'aria-label': 'change time',
              }}
            />
          </MuiPickersUtilsProvider>
        </div>
      </div>
      <ButtonGroup>
        <Button onClick={() => setTooltipOpen(false)}>Cancel</Button>
        <Button onClick={handleClick} variant="outlined">
          Update
        </Button>
      </ButtonGroup>
    </div>
  );
};

export default DateTimeRangePicker;
